import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, FileText, Plus } from 'lucide-react';
import {
  type UserAgreement,
  acceptAgreement,
  listAgreements,
  publishAgreement,
} from '@/api/userAgreements';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

function isAdmin(role: string | undefined): boolean {
  return role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN';
}

export function UserAgreementsPage() {
  const { t } = useTranslation();
  const admin = isAdmin(useAuthStore((s) => s.user?.role));
  const [items, setItems] = useState<UserAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    listAgreements()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function accept(id: string) {
    await acceptAgreement(id);
    setAccepted((prev) => new Set(prev).add(id));
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            {t('userAgreements.title')}
          </h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">
            {t('userAgreements.subtitle')}
          </p>
        </div>
        {admin && (
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="agreement-new">
            <Plus className="mr-1 h-4 w-4" />
            {t('userAgreements.publish')}
          </Button>
        )}
      </header>

      <div className="overflow-hidden rounded-lg border bg-card">
        {loading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}
        {!loading && items.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('userAgreements.empty')}</p>
        )}
        <ul className="divide-y divide-[var(--color-lop-border)]">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-4 py-3" data-testid="agreement-row">
              <FileText className="h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--color-lop-text-primary)]">
                  {a.title}
                </p>
                <p className="text-xs text-[var(--color-lop-text-muted)]">
                  {a.code} · v{a.version}
                </p>
              </div>
              {a.requiredForLogin && (
                <span className="rounded-full bg-amber-900/30 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                  {t('userAgreements.requiredBadge')}
                </span>
              )}
              {accepted.has(a.id) ? (
                <span className="flex items-center gap-1 text-xs text-emerald-400">
                  <Check className="h-4 w-4" />
                  {t('userAgreements.accepted')}
                </span>
              ) : (
                <Button size="sm" variant="outline" onClick={() => accept(a.id)} data-testid="agreement-accept">
                  {t('userAgreements.accept')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {showNew && (
        <PublishModal
          onClose={() => setShowNew(false)}
          onPublished={() => {
            setShowNew(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function PublishModal({ onClose, onPublished }: { onClose: () => void; onPublished: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [version, setVersion] = useState('1.0');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [requiredForLogin, setRequiredForLogin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!code.trim() || !version.trim() || !title.trim() || !body.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await publishAgreement({
        code: code.trim(),
        version: version.trim(),
        title: title.trim(),
        body: body.trim(),
        requiredForLogin,
      });
      onPublished();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to publish');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('userAgreements.publishModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('userAgreements.publishModal.code')}
            </label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="tos" data-testid="agreement-code" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('userAgreements.publishModal.version')}
            </label>
            <Input value={version} onChange={(e) => setVersion(e.target.value)} />
          </div>
        </div>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('userAgreements.publishModal.titleField')}
        </label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mb-3" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('userAgreements.publishModal.body')}
        </label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="mb-3" data-testid="agreement-body" />
        <label className="mb-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={requiredForLogin} onChange={(e) => setRequiredForLogin(e.target.checked)} />
          {t('userAgreements.publishModal.requiredForLogin')}
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="agreement-publish-submit">
            {t('userAgreements.publishModal.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
